import { Stack } from '@hurdl/design-system';
import { Link, useParams } from '@tanstack/react-router';
import { Tags, UtensilsCrossed } from 'lucide-react';
import { useEffect, useState, type ReactElement } from 'react';

import { ViewBookletDialog } from '../bespoke/menu-workspace/review-editor.js';
import {
  ALLERGEN_BOOKLET_URL,
  restaurantLabelOf,
  useMenuOperations,
} from './menus-wiring.js';
import {
  TILE_LINK_CLASS,
  Tile,
  TileHead,
  TileStat,
  TileStats,
} from './tile.js';

/**
 * One Restaurant, the hub into its work (menus-spec R16).
 *
 * A Restaurant owns two things an Operator works on — its reviewed Menu and its managed
 * Categories — so the Restaurant is where both live, each a card linking in, rather than
 * Categories being a single panel-wide page in the sidebar. The Menu card carries the
 * live count of reviewed items; the Categories card carries the live count of managed
 * categories. The source PDF is reachable here too, through the same control the editor
 * uses, so a reviewer can open the booklet from the Restaurant before drilling in.
 *
 * The one `h1` is the page's; the `main` landmark is the Layout's. Every link resolves
 * against the route tree, so no address literal reaches this source.
 */
export function RestaurantPage(): ReactElement {
  const { productKey, restaurantKey } = useParams({ strict: false });
  const key = restaurantKey ?? '';
  const product = productKey ?? '';
  const operations = useMenuOperations();

  const [counts, setCounts] = useState<
    { readonly items: number; readonly categories: number } | undefined
  >(undefined);

  useEffect(() => {
    let live = true;
    void Promise.all([
      operations.listReviewed(key),
      operations.listCategories(key),
    ]).then(([items, categories]) => {
      if (live)
        setCounts({ items: items.length, categories: categories.length });
    });
    return () => {
      live = false;
    };
  }, [operations, key]);

  return (
    <Stack gap="large">
      <Stack
        direction="responsive"
        gap="medium"
        align="centre"
        className="justify-between"
      >
        <h1 className="min-w-0 truncate font-hurdl-display text-hurdl-heading font-hurdl-semibold">
          {restaurantLabelOf(key)}
        </h1>
        <div className="shrink-0">
          <ViewBookletDialog bookletUrl={ALLERGEN_BOOKLET_URL} />
        </div>
      </Stack>

      <div className="grid gap-hurdl-5 sm:grid-cols-2">
        <Link
          to="/products/$productKey/menus/$restaurantKey/items"
          params={{ productKey: product, restaurantKey: key }}
          className={TILE_LINK_CLASS}
        >
          <Tile interactive>
            <TileHead
              icon={UtensilsCrossed}
              title="Menu"
              description="The approved, published items. Edit display, allergens, nutrition and image."
            />
            <TileStats>
              <TileStat label="Published" value={counts?.items} />
            </TileStats>
          </Tile>
        </Link>

        <Link
          to="/products/$productKey/menus/$restaurantKey/categories"
          params={{ productKey: product, restaurantKey: key }}
          className={TILE_LINK_CLASS}
        >
          <Tile interactive>
            <TileHead
              icon={Tags}
              title="Categories"
              description="The managed groupings a reviewer chooses from when categorising an item."
            />
            <TileStats>
              <TileStat label="Managed" value={counts?.categories} />
            </TileStats>
          </Tile>
        </Link>
      </div>
    </Stack>
  );
}
